import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { TouchEvent } from "react";
import DOMPurify from "dompurify";
import { normalizeExternalLink, telegramProfileUrl } from "../lib/telegramLinks";
import { Header } from "../components/Header";
import { ProductGrid } from "../components/ProductGrid";
import { api, type Product, type StoreOption, type StoreSettings } from "../lib/api";

const selectedStoreStorageKey = "telegram-shop:selected-store";
const deviceIdStorageKey = "telegram-shop:device-id";
const productsPageSize = 30;

type Route =
  | { name: "home" }
  | { name: "product"; slug: string }
  | { name: "admin" };

function getRoute(): Route {
  const path = window.location.pathname;
  if (path === "/le-control") return { name: "admin" };
  if (path.startsWith("/products/")) return { name: "product", slug: decodeURIComponent(path.replace("/products/", "")) };
  return { name: "home" };
}

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function App() {
  const [route, setRoute] = useState<Route>(getRoute);
  const [store, setStore] = useState<StoreSettings | null>(null);
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState(() => localStorage.getItem(selectedStoreStorageKey) ?? "");
  const [deviceId] = useState(ensureDeviceId);
  const [products, setProducts] = useState<Product[]>([]);
  const [isAdmin, setAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [isBusy, setBusy] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setRefreshing] = useState(false);
  const [nextProductsOffset, setNextProductsOffset] = useState<number | undefined>(undefined);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const refreshSeq = useRef(0);
  const homeScrollY = useRef(0);
  const shouldRestoreHomeScroll = useRef(false);

  const refreshStorefront = useCallback(async (showInitialLoader = false, storeIdOverride?: string) => {
    const seq = ++refreshSeq.current;
    const requestedStoreId = storeIdOverride ?? selectedStoreId;
    if (showInitialLoader) setBusy(true);
    try {
      const [storeData, storesData] = await Promise.all([api.getStore(), api.getStores()]);
      if (seq !== refreshSeq.current) return;
      const nextSelectedStoreId = resolveSelectedStoreId(
        storesData,
        requestedStoreId,
        storeData.defaultStoreId,
        storeData.forceDefaultStore
      );
      if (nextSelectedStoreId !== selectedStoreId) {
        setSelectedStoreId(nextSelectedStoreId);
        if (nextSelectedStoreId) localStorage.setItem(selectedStoreStorageKey, nextSelectedStoreId);
      }
      const productPage = await api.getProductPage(nextSelectedStoreId, deviceId, 0, productsPageSize);
      if (seq !== refreshSeq.current) return;
      setStore(storeData);
      setStoreOptions(storesData);
      setProducts(productPage.items);
      setNextProductsOffset(productPage.nextOffset);
      setLoadError("");
    } catch (error) {
      if (seq !== refreshSeq.current) return;
      setLoadError(error instanceof Error ? error.message : "Не удалось загрузить витрину");
    } finally {
      if (seq === refreshSeq.current) setBusy(false);
    }
  }, [deviceId, selectedStoreId]);

  useEffect(() => {
    const onPopState = () => setRoute(getRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    refreshStorefront(true);
    const retryId = window.setTimeout(() => refreshStorefront(false), 1500);
    return () => window.clearTimeout(retryId);
  }, [refreshStorefront]);

  useEffect(() => {
    if (!loadError) return undefined;
    const retryId = window.setTimeout(() => refreshStorefront(false), 4000);
    return () => window.clearTimeout(retryId);
  }, [loadError, refreshStorefront]);

  useEffect(() => {
    let lastRefresh = 0;

    function refreshOnResume() {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastRefresh < 3000) return;
      lastRefresh = now;
      refreshStorefront();
    }

    window.addEventListener("focus", refreshOnResume);
    window.addEventListener("pageshow", refreshOnResume);
    window.addEventListener("online", refreshOnResume);
    document.addEventListener("visibilitychange", refreshOnResume);

    return () => {
      window.removeEventListener("focus", refreshOnResume);
      window.removeEventListener("pageshow", refreshOnResume);
      window.removeEventListener("online", refreshOnResume);
      document.removeEventListener("visibilitychange", refreshOnResume);
    };
  }, [refreshStorefront]);

  useEffect(() => {
    if (route.name !== "admin") return;
    setAdminChecked(false);
    api
      .getAdminMe()
      .then(() => setAdmin(true))
      .catch(() => setAdmin(false))
      .finally(() => setAdminChecked(true));
  }, [route]);

  const visibleProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          product.visibility === "published" &&
          product.availabilityStatus !== "hidden" &&
          (!selectedStoreId || product.sourceChannelId === selectedStoreId)
      ),
    [products, selectedStoreId]
  );
  const selectedStore = useMemo(
    () => storeOptions.find((item) => item.id === selectedStoreId) ?? null,
    [selectedStoreId, storeOptions]
  );
  const displayStore = selectedStore ?? store;

  useLayoutEffect(() => {
    if (route.name !== "home" || !shouldRestoreHomeScroll.current || isBusy || loadError) return;
    const targetY = homeScrollY.current;
    shouldRestoreHomeScroll.current = false;
    window.requestAnimationFrame(() => {
      window.scrollTo(0, targetY);
      window.requestAnimationFrame(() => window.scrollTo(0, targetY));
    });
  }, [isBusy, loadError, route.name, visibleProducts.length]);

  function handleStoreChange(storeId: string) {
    localStorage.setItem(selectedStoreStorageKey, storeId);
    setSelectedStoreId(storeId);
    setProducts([]);
    setNextProductsOffset(undefined);
    homeScrollY.current = 0;
    shouldRestoreHomeScroll.current = true;
    if (route.name !== "home") navigate("/");
    void refreshStorefront(false, storeId);
  }

  function handleOpenProduct(slug: string) {
    homeScrollY.current = window.scrollY;
    shouldRestoreHomeScroll.current = true;
    navigate(`/products/${slug}`);
  }

  function handleReturnHome() {
    if (!shouldRestoreHomeScroll.current) {
      homeScrollY.current = 0;
      shouldRestoreHomeScroll.current = true;
    }
    navigate("/");
  }

  const loadMoreProducts = useCallback(async () => {
    if (isLoadingMore || nextProductsOffset === undefined) return;
    setLoadingMore(true);
    try {
      const page = await api.getProductPage(selectedStoreId, deviceId, nextProductsOffset, productsPageSize);
      setProducts((current) => {
        const existingIds = new Set(current.map((product) => product.id));
        return [...current, ...page.items.filter((product) => !existingIds.has(product.id))];
      });
      setNextProductsOffset(page.nextOffset);
    } finally {
      setLoadingMore(false);
    }
  }, [deviceId, isLoadingMore, nextProductsOffset, selectedStoreId]);

  function handleTouchStart(event: TouchEvent<HTMLElement>) {
    if (window.scrollY > 0) return;
    touchStartY.current = event.touches[0]?.clientY ?? null;
  }

  function handleTouchMove(event: TouchEvent<HTMLElement>) {
    if (touchStartY.current === null || window.scrollY > 0) return;
    const currentY = event.touches[0]?.clientY ?? 0;
    const distance = Math.max(0, currentY - touchStartY.current);
    if (distance > 8) setPullDistance(Math.min(96, Math.round(distance * 0.55)));
  }

  async function handleTouchEnd() {
    touchStartY.current = null;
    if (pullDistance < 64 || isRefreshing) {
      setPullDistance(0);
      return;
    }

    setRefreshing(true);
    setPullDistance(72);
    await refreshStorefront();
    setRefreshing(false);
    setPullDistance(0);
  }

  if (route.name === "admin") {
    return (
      <main className="app-shell">
        <AdminView
          stores={storeOptions}
          onBack={handleReturnHome}
          isAdmin={isAdmin}
          adminChecked={adminChecked}
          onAuthChange={(authenticated) => {
            setAdmin(authenticated);
            setAdminChecked(true);
          }}
        />
      </main>
    );
  }

  if (isBusy) return <main className="app-shell loading">Загружаем витрину...</main>;

  if (loadError || !store) {
    return (
      <main className="app-shell loading">
        <div className="locked-panel">
          <h3>Витрина не подключена</h3>
          <p>{loadError || "Не удалось загрузить настройки магазина."}</p>
          <button className="text-button" onClick={() => refreshStorefront(true)}>
            Повторить
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      className="app-shell"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStartY.current = null;
        setPullDistance(0);
      }}
    >
      <div
        className={`pull-refresh ${pullDistance > 0 ? "visible" : ""}`}
        style={{ transform: `translate(-50%, ${pullDistance - 72}px)` }}
        aria-hidden="true"
      >
        {isRefreshing ? "Обновляем..." : "Потяните для обновления"}
      </div>
      {route.name !== "product" ? (
        <Header
          store={displayStore ?? store}
          stores={storeOptions}
          selectedStoreId={selectedStoreId}
          onStoreChange={handleStoreChange}
        />
      ) : null}
      {route.name === "home" || route.name === "product" ? (
        <ProductGrid
          products={visibleProducts}
          onOpen={handleOpenProduct}
          onLoadMore={loadMoreProducts}
          hasMore={nextProductsOffset !== undefined}
          isLoadingMore={isLoadingMore}
          preserved={route.name === "product"}
        />
      ) : null}
      {route.name === "product" ? (
        <ProductFeed
          products={visibleProducts}
          initialSlug={route.slug}
          stores={storeOptions}
          selectedStoreId={selectedStoreId}
          deviceId={deviceId}
          onLoadMore={loadMoreProducts}
          hasMore={nextProductsOffset !== undefined}
          isLoadingMore={isLoadingMore}
          onStoreChange={handleStoreChange}
          onBack={handleReturnHome}
        />
      ) : null}
    </main>
  );
}

function ProductFeed({
  products,
  initialSlug,
  stores,
  selectedStoreId,
  deviceId,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onStoreChange,
  onBack
}: {
  products: Product[];
  initialSlug: string;
  stores: StoreOption[];
  selectedStoreId: string;
  deviceId: string;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onStoreChange: (storeId: string) => void;
  onBack: () => void;
}) {
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const didScrollToInitial = useRef(false);

  useEffect(() => {
    didScrollToInitial.current = false;
  }, [initialSlug]);

  useEffect(() => {
    if (didScrollToInitial.current || products.length === 0) return;
    const target = itemRefs.current.get(initialSlug);
    if (!target) return;
    didScrollToInitial.current = true;
    requestAnimationFrame(() => target.scrollIntoView({ block: "start" }));
  }, [initialSlug, products]);

  useEffect(() => {
    if (products.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const slug = visible?.target.getAttribute("data-slug");
        const currentRoute = getRoute();
        if (!slug || currentRoute.name !== "product" || currentRoute.slug === slug) return;
        window.history.replaceState({}, "", `/products/${encodeURIComponent(slug)}`);
      },
      { threshold: [0.45, 0.7] }
    );

    itemRefs.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [products]);

  return (
    <section className="product-feed" aria-label="Лента товаров">
      <div className="feed-topbar">
        <button className="text-button" onClick={onBack}>
          Назад
        </button>
        <label className="store-switcher compact">
          <span>Канал:</span>
          <select value={selectedStoreId} onChange={(event) => onStoreChange(event.target.value)}>
            {stores.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {products.length > 0 ? (
        products.map((product) => (
          <ProductFeedItem
            key={product.id}
            product={product}
            deviceId={deviceId}
            refCallback={(element) => {
              if (element) itemRefs.current.set(product.slug, element);
              else itemRefs.current.delete(product.slug);
            }}
          />
        ))
      ) : (
        <div className="locked-panel">Пока нет опубликованных товаров.</div>
      )}
      {hasMore ? (
        <button className="feed-load-more" onClick={onLoadMore} disabled={isLoadingMore}>
          {isLoadingMore ? "Загружаем..." : "Показать еще"}
        </button>
      ) : null}
    </section>
  );
}

function resolveSelectedStoreId(stores: StoreOption[], currentId: string, defaultStoreId?: string, forceDefaultStore = false) {
  if (stores.length === 0) return "";
  if (forceDefaultStore && defaultStoreId && stores.some((item) => item.id === defaultStoreId)) return defaultStoreId;
  if (!forceDefaultStore && stores.some((item) => item.id === currentId)) return currentId;
  if (defaultStoreId && stores.some((item) => item.id === defaultStoreId)) return defaultStoreId;
  return stores[0].id;
}

function ProductFeedItem({
  product,
  deviceId,
  refCallback
}: {
  product: Product;
  deviceId: string;
  refCallback: (element: HTMLElement | null) => void;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [liked, setLiked] = useState(Boolean(product.viewerHasLiked));
  const [likeCount, setLikeCount] = useState(product.likeCount ?? 0);
  const [likeBurst, setLikeBurst] = useState(false);
  const [lightboxScale, setLightboxScale] = useState(1);
  const [lightboxOffset, setLightboxOffset] = useState({ x: 0, y: 0 });
  const lightboxTouchStart = useRef<{ x: number; y: number } | null>(null);
  const lightboxPinch = useRef<{ distance: number; scale: number } | null>(null);
  const lightboxPan = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const photoOpenTimer = useRef<number | null>(null);
  const lastPhotoTouch = useRef<{ index: number; time: number } | null>(null);
  const fallbackSeller = extractSellerFromDescription(product.description);
  const descriptionHtml = prepareDescriptionHtml(product.description, product.sellerContactUrl ?? fallbackSeller?.url);

  useEffect(() => {
    setLiked(Boolean(product.viewerHasLiked));
    setLikeCount(product.likeCount ?? 0);
    setDescriptionExpanded(false);
  }, [product.id, product.likeCount, product.viewerHasLiked]);

  useEffect(() => {
    return () => {
      if (photoOpenTimer.current !== null) window.clearTimeout(photoOpenTimer.current);
    };
  }, []);

  function moveLightbox(delta: number) {
    resetLightboxTransform();
    setLightboxIndex((current) => {
      if (current === null) return current;
      return (current + delta + product.media.length) % product.media.length;
    });
  }

  function resetLightboxTransform() {
    setLightboxScale(1);
    setLightboxOffset({ x: 0, y: 0 });
    lightboxPan.current = null;
    lightboxPinch.current = null;
  }

  function closeLightbox() {
    resetLightboxTransform();
    setLightboxIndex(null);
  }

  function handleLightboxTouchStart(event: TouchEvent<HTMLDivElement>) {
    if (event.touches.length >= 2) {
      lightboxPinch.current = {
        distance: touchDistance(event.touches[0], event.touches[1]),
        scale: lightboxScale
      };
      lightboxTouchStart.current = null;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;
    if (lightboxScale > 1) {
      lightboxPan.current = {
        x: touch.clientX,
        y: touch.clientY,
        offsetX: lightboxOffset.x,
        offsetY: lightboxOffset.y
      };
      lightboxTouchStart.current = null;
      return;
    }

    lightboxTouchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function handleLightboxTouchMove(event: TouchEvent<HTMLDivElement>) {
    if (event.touches.length >= 2 && lightboxPinch.current) {
      event.preventDefault();
      event.stopPropagation();
      const nextDistance = touchDistance(event.touches[0], event.touches[1]);
      const nextScale = clamp(lightboxPinch.current.scale * (nextDistance / lightboxPinch.current.distance), 1, 3);
      setLightboxScale(nextScale);
      setLightboxOffset((current) => clampLightboxOffset(current, nextScale));
      return;
    }

    if (lightboxScale > 1 && event.touches.length === 1) {
      event.preventDefault();
      event.stopPropagation();
      const touch = event.touches[0];
      const pan = lightboxPan.current;
      if (!touch || !pan) return;
      setLightboxOffset(
        clampLightboxOffset(
          {
            x: pan.offsetX + touch.clientX - pan.x,
            y: pan.offsetY + touch.clientY - pan.y
          },
          lightboxScale
        )
      );
    }
  }

  function handleLightboxTouchEnd(event: TouchEvent<HTMLDivElement>) {
    if (lightboxPinch.current) {
      lightboxPinch.current = null;
      lightboxPan.current = null;
      lightboxTouchStart.current = null;
      if (lightboxScale < 1.05) resetLightboxTransform();
      else setLightboxOffset((current) => clampLightboxOffset(current, lightboxScale));
      return;
    }

    if (lightboxScale > 1) {
      lightboxPan.current = null;
      lightboxTouchStart.current = null;
      setLightboxOffset((current) => clampLightboxOffset(current, lightboxScale));
      return;
    }

    const start = lightboxTouchStart.current;
    lightboxTouchStart.current = null;
    const touch = event.changedTouches[0];
    if (!start || !touch || product.media.length < 2) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.3) return;

    event.stopPropagation();
    moveLightbox(deltaX < 0 ? 1 : -1);
  }

  function schedulePhotoOpen(index: number) {
    if (photoOpenTimer.current !== null) window.clearTimeout(photoOpenTimer.current);
    photoOpenTimer.current = window.setTimeout(() => {
      setLightboxIndex(index);
      photoOpenTimer.current = null;
    }, 260);
  }

  function handlePhotoTouchEnd(event: TouchEvent<HTMLButtonElement>, index: number) {
    const now = Date.now();
    const last = lastPhotoTouch.current;
    lastPhotoTouch.current = { index, time: now };
    if (!last || last.index !== index || now - last.time > 280) return;

    event.preventDefault();
    event.stopPropagation();
    lastPhotoTouch.current = null;
    likeProduct();
  }

  async function likeProduct() {
    if (photoOpenTimer.current !== null) {
      window.clearTimeout(photoOpenTimer.current);
      photoOpenTimer.current = null;
    }

    setLikeBurst(true);
    window.setTimeout(() => setLikeBurst(false), 520);
    if (liked) return;

    setLiked(true);
    setLikeCount((count) => count + 1);

    try {
      const state = await api.likeProduct(product.id, deviceId);
      setLiked(state.viewerHasLiked);
      setLikeCount(state.likeCount);
    } catch {
      setLiked(false);
      setLikeCount((count) => Math.max(0, count - 1));
    }
  }

  return (
    <article className="feed-item" data-slug={product.slug} ref={refCallback}>
      <div className="media-strip">
        {product.media.length > 0 ? (
          product.media.map((media, index) => (
            <button
              className="media-thumb"
              key={media.id}
              onClick={() => schedulePhotoOpen(index)}
              onDoubleClick={(event) => {
                event.preventDefault();
                likeProduct();
              }}
              onTouchEnd={(event) => handlePhotoTouchEnd(event, index)}
              aria-label="Открыть фото"
            >
              <img
                src={media.url}
                alt={media.altText ?? product.title}
                loading="lazy"
                decoding="async"
                onError={(event) => {
                  event.currentTarget.src = "/placeholders/product.svg";
                }}
              />
              {product.media.length > 1 ? (
                <span className="media-count">
                  {index + 1} / {product.media.length}
                </span>
              ) : null}
              <span className={`like-burst ${likeBurst ? "visible" : ""}`} aria-hidden="true">
                ♥
              </span>
            </button>
          ))
        ) : (
          <button className="media-thumb" onClick={() => undefined} aria-label="Фото отсутствует">
            <img src="/placeholders/product.svg" alt="" loading="lazy" decoding="async" />
          </button>
        )}
      </div>
      <div className="detail-copy">
        <div className="feed-actions">
          <button
            className={`heart-button ${liked ? "liked" : ""}`}
            onClick={likeProduct}
            aria-label={liked ? "Уже нравится" : "Поставить отметку нравится"}
            aria-pressed={liked}
          >
            {liked ? "♥" : "♡"}
          </button>
          <span className="like-row">{formatLikes(likeCount)}</span>
        </div>
        <div
          className={`formatted-description collapsible-description ${descriptionExpanded ? "expanded" : ""}`}
          onClick={(event) => {
            if ((event.target as Element).closest("a")) return;
            setDescriptionExpanded((value) => !value);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            setDescriptionExpanded((value) => !value);
          }}
          dangerouslySetInnerHTML={{ __html: descriptionHtml }}
        />
        {!descriptionExpanded ? (
          <button className="description-more" onClick={() => setDescriptionExpanded(true)}>
            еще
          </button>
        ) : null}
      </div>
      {lightboxIndex !== null && product.media[lightboxIndex] ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
          onTouchStart={handleLightboxTouchStart}
          onTouchMove={handleLightboxTouchMove}
          onTouchEnd={handleLightboxTouchEnd}
          onTouchCancel={() => {
            lightboxTouchStart.current = null;
            lightboxPinch.current = null;
            lightboxPan.current = null;
          }}
        >
          <button
            className="lightbox-close"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closeLightbox();
            }}
            onTouchStart={(event) => {
              event.stopPropagation();
            }}
            onTouchEnd={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closeLightbox();
            }}
            aria-label="Закрыть"
          >
            ×
          </button>
          <img
            src={product.media[lightboxIndex].url}
            alt={product.media[lightboxIndex].altText ?? product.title}
            decoding="async"
            style={{ transform: `translate3d(${lightboxOffset.x}px, ${lightboxOffset.y}px, 0) scale(${lightboxScale})` }}
            onClick={(event) => event.stopPropagation()}
            onError={(event) => {
              event.currentTarget.src = "/placeholders/product.svg";
            }}
          />
          <div className="lightbox-count">
            {lightboxIndex + 1} / {product.media.length}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function AdminView({
  stores,
  onBack,
  isAdmin,
  adminChecked,
  onAuthChange
}: {
  stores: StoreOption[];
  onBack: () => void;
  isAdmin: boolean;
  adminChecked: boolean;
  onAuthChange: (authenticated: boolean) => void;
}) {
  const [drafts, setDrafts] = useState<Product[]>([]);
  const [password, setPassword] = useState("");
  const [query, setQuery] = useState("");
  const [storeId, setStoreId] = useState("");
  const [nextOffset, setNextOffset] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const loadProducts = useCallback(async (append = false) => {
    if (!isAdmin) return;
    setLoading(true);
    setMessage("");
    try {
      const page = await api.getAdminProducts({ query, storeId, offset: append ? nextOffset ?? 0 : 0, limit: 40 });
      setDrafts((items) => (append ? [...items, ...page.items] : page.items));
      setNextOffset(page.nextOffset);
    } catch {
      setMessage("Не удалось загрузить объявления.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, nextOffset, query, storeId]);

  useEffect(() => {
    if (isAdmin) void loadProducts();
  }, [isAdmin]);

  async function login(event: React.FormEvent) {
    event.preventDefault();
    setMessage("Проверяем пароль...");
    try {
      await api.adminLogin(password);
      setPassword("");
      setMessage("");
      onAuthChange(true);
    } catch {
      setMessage("Неверный пароль или слишком много попыток.");
    }
  }

  async function logout() {
    await api.adminLogout().catch(() => undefined);
    setDrafts([]);
    onAuthChange(false);
  }

  function updateDraft(id: string, patch: Partial<Product>) {
    setDrafts((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  async function saveProduct(product: Product) {
    setMessage("Сохраняем...");
    try {
      const saved = await api.updateProduct(product.id, {
        title: product.title,
        description: product.description,
        price: product.price ?? null,
        currency: product.currency,
        availabilityStatus: product.availabilityStatus,
        visibility: product.visibility,
        expiresAt: product.expiresAt
      });
      updateDraft(saved.id, saved);
      setMessage("Сохранено");
    } catch {
      setMessage("Не удалось сохранить. Проверьте заполненные поля.");
    }
  }

  async function toggleVisibility(product: Product) {
    const visibility = product.visibility === "published" ? "hidden" : "published";
    try {
      const saved = await api.updateProduct(product.id, { visibility });
      updateDraft(product.id, saved);
      setMessage(visibility === "hidden" ? "Объявление скрыто" : "Объявление опубликовано");
    } catch {
      setMessage("Не удалось изменить видимость.");
    }
  }

  async function deleteProduct(product: Product) {
    if (!window.confirm(`Удалить «${product.title}» навсегда вместе с фотографиями?`)) return;
    try {
      await api.deleteProduct(product.id);
      setDrafts((items) => items.filter((item) => item.id !== product.id));
      setMessage("Объявление удалено");
    } catch {
      setMessage("Не удалось удалить объявление.");
    }
  }

  return (
    <section className="admin-panel">
      <div className="admin-heading">
        <button className="text-button" onClick={onBack}>
          Назад
        </button>
        <h2>Управление объявлениями</h2>
        {isAdmin ? <button className="text-button" onClick={logout}>Выйти</button> : null}
      </div>
      {!adminChecked ? <div className="locked-panel">Проверяем доступ...</div> : null}
      {adminChecked && !isAdmin ? (
        <form className="locked-panel admin-login" onSubmit={login}>
          <h3>Вход</h3>
          <label htmlFor="admin-password">Пароль</label>
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <button className="primary-link" type="submit">Войти</button>
        </form>
      ) : null}
      {message ? <p className="admin-message" role="status">{message}</p> : null}
      {adminChecked && isAdmin ? (
        <>
          <form
            className="admin-filters"
            onSubmit={(event) => {
              event.preventDefault();
              void loadProducts();
            }}
          >
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по тексту" />
            <select value={storeId} onChange={(event) => setStoreId(event.target.value)} aria-label="Канал">
              <option value="">Все каналы</option>
              {stores.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <button className="text-button" type="submit">Найти</button>
          </form>
          {drafts.map((product) => (
            <article className="admin-product" key={product.id}>
              <img
                src={product.media[0]?.url ?? "/placeholders/product.svg"}
                alt=""
                loading="lazy"
                decoding="async"
                onError={(event) => { event.currentTarget.src = "/placeholders/product.svg"; }}
              />
              <label>
                Название
                <input
                  value={product.title}
                  onChange={(event) => updateDraft(product.id, { title: event.target.value })}
                />
              </label>
              <label>
                Описание
                <textarea
                  value={product.description}
                  onChange={(event) => updateDraft(product.id, { description: event.target.value })}
                />
              </label>
              <label>
                Цена
                <input
                  type="number"
                  value={product.price ?? ""}
                  onChange={(event) => updateDraft(product.id, { price: event.target.value === "" ? undefined : Number(event.target.value) })}
                />
              </label>
              <label>
                Видимость
                <select
                  value={product.visibility}
                  onChange={(event) => updateDraft(product.id, { visibility: event.target.value as Product["visibility"] })}
                >
                  <option value="published">Опубликован</option>
                  <option value="hidden">Скрыт</option>
                </select>
              </label>
              <label>
                Удалить после
                <input
                  type="datetime-local"
                  value={toLocalDateTimeValue(product.expiresAt)}
                  onChange={(event) => updateDraft(product.id, { expiresAt: new Date(event.target.value).toISOString() })}
                />
              </label>
              <div className="admin-actions">
                <button className="primary-link" onClick={() => saveProduct(product)}>Сохранить</button>
                <button className="text-button" onClick={() => toggleVisibility(product)}>
                  {product.visibility === "published" ? "Скрыть" : "Опубликовать"}
                </button>
                <button className="danger-button" onClick={() => deleteProduct(product)}>Удалить</button>
              </div>
            </article>
          ))}
          {drafts.length === 0 && !loading ? <p>Объявления не найдены.</p> : null}
          {nextOffset !== undefined ? (
            <button className="text-button admin-load-more" disabled={loading} onClick={() => loadProducts(true)}>
              {loading ? "Загружаем..." : "Показать ещё"}
            </button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function toLocalDateTimeValue(value: string) {
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function statusLabel(status: Product["availabilityStatus"]) {
  const labels: Record<Product["availabilityStatus"], string> = {
    available: "В наличии",
    reserved: "Зарезервировано",
    sold: "Продано",
    hidden: "Скрыто"
  };
  return labels[status];
}

function extractSellerFromDescription(description: string) {
  const decoded = decodeHtmlBreaks(description);
  const match = decoded.match(/(?:^|[\s(])@([A-Za-z0-9_]{5,32})\b/);
  if (!match?.[1]) return null;
  return {
    username: match[1],
    label: `Написать @${match[1]}`,
    url: telegramProfileUrl(match[1])
  };
}

function normalizeDescriptionHtml(description: string) {
  return decodeHtmlBreaks(description)
    .replace(/^(?:<br>\s*)+|(?:\s*<br>)+$/g, "")
    .replace(/(?:<br>\s*){2,}/g, "<br>");
}

function decodeHtmlBreaks(value: string) {
  return value.replace(/&lt;br\s*\/?&gt;/gi, "<br>").replace(/<br\s*\/?>/gi, "<br>");
}

function sanitizeDescriptionHtml(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["a", "br", "strong", "em", "u", "s", "code", "pre"],
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|tg):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
  });
}

function prepareDescriptionHtml(description: string, sellerContactUrl?: string) {
  let html = normalizeDescriptionHtml(description);
  if (sellerContactUrl && !html.includes(sellerContactUrl) && !/@[A-Za-z0-9_]{5,32}\b/.test(html)) {
    html = `${html}<br>${sellerContactUrl}`;
  }
  return linkifyHtml(sanitizeDescriptionHtml(html));
}

function linkifyHtml(html: string) {
  if (typeof DOMParser === "undefined") return sanitizeDescriptionHtml(html);

  const parser = new DOMParser();
  const document = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = document.body.firstElementChild;
  if (!root) return sanitizeDescriptionHtml(html);

  linkifyNode(document, root);
  return sanitizeDescriptionHtml(root.innerHTML);
}

function linkifyNode(document: Document, node: Node) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const tagName = (node as Element).tagName.toLowerCase();
    if (["a", "code", "pre"].includes(tagName)) return;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? "";
    const fragment = linkifyText(document, text);
    if (fragment) node.parentNode?.replaceChild(fragment, node);
    return;
  }

  Array.from(node.childNodes).forEach((child) => linkifyNode(document, child));
}

function linkifyText(document: Document, text: string) {
  const pattern =
    /(https?:\/\/[^\s<]+|(?:www\.)?(?:t\.me|telegram\.me|vk\.com|vk\.ru|instagram\.com)\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]+|@([A-Za-z0-9_]{5,32})\b)/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const fragment = document.createDocumentFragment();
  let changed = false;

  while ((match = pattern.exec(text))) {
    const rawValue = match[0];
    const suffix = rawValue.match(/[.,;:!?)]*$/)?.[0] ?? "";
    const value = suffix ? rawValue.slice(0, -suffix.length) : rawValue;
    const valueStart = match.index;
    if (!value) continue;

    fragment.append(document.createTextNode(text.slice(lastIndex, valueStart)));
    const anchor = document.createElement("a");
    anchor.href = normalizeExternalLink(value, match[2]);
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
    anchor.textContent = value;
    fragment.append(anchor);
    if (suffix) fragment.append(document.createTextNode(suffix));
    lastIndex = match.index + rawValue.length;
    changed = true;
  }

  if (!changed) return null;
  fragment.append(document.createTextNode(text.slice(lastIndex)));
  return fragment;
}

function touchDistance(first: { clientX: number; clientY: number }, second: { clientX: number; clientY: number }) {
  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function clampLightboxOffset(offset: { x: number; y: number }, scale: number) {
  if (scale <= 1) return { x: 0, y: 0 };
  const maxX = (window.innerWidth * (scale - 1)) / 2;
  const maxY = (window.innerHeight * (scale - 1)) / 2;
  return {
    x: clamp(offset.x, -maxX, maxX),
    y: clamp(offset.y, -maxY, maxY)
  };
}

function ensureDeviceId() {
  const existing = localStorage.getItem(deviceIdStorageKey);
  if (existing) return existing;
  const value = crypto.randomUUID();
  localStorage.setItem(deviceIdStorageKey, value);
  return value;
}

function formatLikes(count: number) {
  const word = new Intl.PluralRules("ru-RU").select(count);
  const label = word === "one" ? "отметка нравится" : word === "few" ? "отметки нравится" : "отметок нравится";
  return `${count} ${label}`;
}
